/**
 * Workshop showcase for StageRail — a right-edge Stage-Manager-style rail
 * for running subagent conversations.
 *
 * Mocks are static (no timers): production toasts auto-hide (working 3s,
 * done 10s), so the rail keeps finished work visible until it ages out. The
 * "show running only" toggle plus the pre-aged-out item below demonstrate
 * that behavior without any timers.
 *
 * Props-in, JSX-out. Local UI state only (active conversation, toggle).
 */

import { useState, type ReactNode } from "react";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import {
  StageRail,
  type StageRailItem,
} from "@/workshop/components/cursors/stage-rail/stage-rail";

const MOCK_RUNNING: StageRailItem[] = [
  {
    conversationId: "conv-calendar",
    agentName: "Calendar agent",
    appLabel: "Calendar",
    icon: "Calendar",
    state: "working",
    detail: "Scanning next week for open focus blocks across three calendars…",
    nextAction: "Propose 3 rescheduled slots",
  },
  {
    conversationId: "conv-gmail",
    agentName: "Gmail agent",
    appLabel: "Gmail",
    icon: "Mail",
    state: "question",
    detail:
      "Found 2 threads about the launch invoice with conflicting totals — which one should I summarize?",
    nextAction: "Waiting on your pick",
  },
  {
    conversationId: "conv-slack",
    agentName: "Slack agent",
    appLabel: "Slack",
    icon: "MessageSquare",
    state: "done",
    detail: "Posted the standup recap to #team-dolcetto with action items.",
    nextAction: "Ages out of the rail shortly",
  },
  {
    conversationId: "conv-code",
    agentName: "Code agent",
    appLabel: "Code",
    icon: "Code",
    state: "approval",
    detail:
      "Refactor touches auth and billing — 14 files, tests green. Ready to apply?",
    nextAction: "Approve or request changes",
  },
];

// Already aged out: finished earlier, removed from the rail (static state,
// no timers). Listed here so reviewers can see what left.
const MOCK_AGED_OUT: StageRailItem = {
  conversationId: "conv-notes",
  agentName: "Notes agent",
  appLabel: "Notes",
  icon: "FileText",
  state: "done",
  detail: "Cleaned up preview screenshots from yesterday's session.",
};

function mockById(conversationId: string): StageRailItem {
  const item = MOCK_RUNNING.find(
    (entry) => entry.conversationId === conversationId,
  );
  if (!item) throw new Error(`Missing stage-rail mock ${conversationId}`);
  return item;
}

const MOCK_SINGLE: StageRailItem[] = [mockById("conv-calendar")];

const MOCK_MANY: StageRailItem[] = [
  ...MOCK_RUNNING,
  {
    conversationId: "conv-docs",
    agentName: "Docs agent",
    appLabel: "Docs",
    icon: "FileText",
    state: "working",
    detail: "Drafting the migration guide from the release notes…",
    nextAction: "Outline ready for review",
  },
  {
    conversationId: "conv-tasks",
    agentName: "Tasks agent",
    appLabel: "Tasks",
    icon: "ListTodo",
    state: "blocked",
    detail: "Waiting on repo access before triaging the backlog.",
    nextAction: "Grant access to continue",
  },
  {
    conversationId: "conv-search",
    agentName: "Search agent",
    appLabel: "Search",
    icon: "Search",
    state: "failed",
    detail: "Provider timed out twice — retry from the conversation.",
    nextAction: "Retry run",
  },
];

const MOCK_ALL_DONE: StageRailItem[] = [
  mockById("conv-slack"),
  {
    conversationId: "conv-docs-done",
    agentName: "Docs agent",
    appLabel: "Docs",
    icon: "FileText",
    state: "done",
    detail: "Migration guide exported to the shared folder.",
    nextAction: "Ages out of the rail shortly",
  },
  {
    conversationId: "conv-mail-done",
    agentName: "Gmail agent",
    appLabel: "Gmail",
    icon: "Mail",
    state: "done",
    detail: "Invoice summary sent to finance.",
    nextAction: "Ages out of the rail shortly",
  },
];

function isRunning(item: StageRailItem): boolean {
  return item.state !== "done" && item.state !== "failed";
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col rounded-2xl border border-glass-stroke-light bg-glass-fill/35 p-5 shadow-glass backdrop-blur-glass-sm">
      <div className="mb-5">
        <h2 className="text-sm font-semibold text-primary">{title}</h2>
        <p className="mt-1 max-w-xl text-xs leading-5 text-secondary">
          {description}
        </p>
      </div>
      {children}
    </section>
  );
}

function DesktopPlaceholder({ label }: { label: string }) {
  return (
    <div className="flex flex-1 items-center justify-center p-10 pr-16">
      <div className="w-full max-w-[420px] rounded-xl border border-dashed border-glass-stroke-light bg-glass-fill/25 p-6 text-center">
        <p className="text-sm font-medium text-secondary">{label}</p>
        <p className="mt-1 text-xs leading-5 text-muted">
          Hover the right edge to reveal the rail.
        </p>
      </div>
    </div>
  );
}

function ActivePreview({ item }: { item: StageRailItem | undefined }) {
  if (!item) {
    return (
      <div
        data-testid="stage-rail-page-preview"
        className="mx-10 mb-8 rounded-preview border border-glass-stroke-subtle bg-elevated/60 px-4 py-3"
      >
        <p className="text-xs text-muted">
          Nothing selected — click a rail card to preview it here.
        </p>
      </div>
    );
  }
  return (
    <div
      data-testid="stage-rail-page-preview"
      data-conversation-id={item.conversationId}
      className="mx-10 mb-8 rounded-preview border border-glass-stroke-subtle bg-elevated/60 px-4 py-3"
    >
      <div className="flex items-center gap-2">
        <p className="text-xs font-semibold text-primary">{item.agentName}</p>
        <span className="text-[11px] text-muted">· {item.appLabel}</span>
      </div>
      <p className="mt-1 line-clamp-2 text-xs leading-5 text-secondary">
        {item.detail}
      </p>
      {item.nextAction && (
        <p className="mt-1 text-[11px] text-muted">{item.nextAction}</p>
      )}
    </div>
  );
}

function MiniDesktop({
  title,
  description,
  items,
  testId,
}: {
  title: string;
  description: string;
  items: StageRailItem[];
  testId: string;
}) {
  const [activeId, setActiveId] = useState(items[0]?.conversationId);
  const active = items.find((item) => item.conversationId === activeId);
  return (
    <div data-testid={testId} className="flex flex-col gap-2">
      <div className="relative flex h-72 flex-col overflow-hidden rounded-preview border border-glass-stroke-subtle bg-canvas">
        <div className="flex flex-1 items-center justify-center p-6 pr-14">
          <p className="max-w-[220px] text-center text-xs leading-5 text-muted">
            {active
              ? `${active.agentName}: ${active.detail}`
              : "No running conversations."}
          </p>
        </div>
        <StageRail
          items={items}
          activeId={activeId}
          onSwitch={setActiveId}
          onFollowUp={setActiveId}
        />
      </div>
      <div>
        <h3 className="text-xs font-medium text-primary">{title}</h3>
        <p className="mt-0.5 text-[11px] leading-4 text-muted">{description}</p>
      </div>
    </div>
  );
}

export function StageRailPage() {
  const [activeId, setActiveId] = useState(
    mockById("conv-calendar").conversationId,
  );
  const [showRunningOnly, setShowRunningOnly] = useState(true);

  const visibleItems = showRunningOnly
    ? MOCK_RUNNING.filter(isRunning)
    : MOCK_RUNNING;
  const active =
    MOCK_RUNNING.find((item) => item.conversationId === activeId) ??
    visibleItems[0];

  return (
    <div className="min-h-screen bg-canvas px-8 py-7 text-primary">
      <div className="mx-auto flex max-w-6xl flex-col gap-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-accent">
            Cursors
          </p>
          <h1 className="mt-2 text-2xl font-semibold">Stage Rail</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-secondary">
            A right-edge Stage-Manager-style rail for running subagent
            conversations. Production toasts auto-hide, so this rail keeps
            mid-process work one hover away: per conversation a card with app
            icon, agent name, status, preview, and next action.
          </p>
          <button
            type="button"
            aria-pressed={showRunningOnly}
            data-testid="stage-rail-page-running-toggle"
            onClick={(event) => {
              event.stopPropagation();
              setShowRunningOnly((value) => !value);
            }}
            onPointerDown={(event) => event.stopPropagation()}
            className={cn(
              "nodrag mt-4 flex items-center gap-2 rounded-control border px-3 py-1.5 text-xs text-secondary transition-colors hover:text-primary",
              showRunningOnly
                ? "border-accent/40 bg-accent/10"
                : "border-glass-stroke-subtle bg-glass-fill/40",
            )}
          >
            <Icons.Eye className="h-3.5 w-3.5" />
            {showRunningOnly ? "Showing running only" : "Showing all states"}
          </button>
          <p className="mt-2 max-w-2xl text-[11px] leading-4 text-muted">
            Already aged out (static state, no timers): {MOCK_AGED_OUT.agentName}
            {" — "}
            {MOCK_AGED_OUT.detail}
          </p>
        </div>

        <Section
          title="Live prototype"
          description="Simulated desktop with four running conversations. Hover the right edge to reveal the rail, then click a card to switch the active preview below."
        >
          <div
            data-testid="stage-rail-page-desktop"
            className="relative flex min-h-[520px] flex-1 flex-col overflow-hidden rounded-[28px] border border-glass-stroke-subtle bg-canvas"
          >
            <DesktopPlaceholder label="Main content" />
            <ActivePreview item={active} />
            <StageRail
              items={visibleItems}
              activeId={active?.conversationId}
              onSwitch={setActiveId}
              onFollowUp={setActiveId}
            />
          </div>
        </Section>

        <Section
          title="Edge cases"
          description="Empty, single, many (scrolls), and all-done rails in small desktops."
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <MiniDesktop
              title="Empty — no running"
              description="Collapsed strip shows a sparkle; hover reveals the empty state."
              items={[]}
              testId="stage-rail-page-edge-empty"
            />
            <MiniDesktop
              title="Single item"
              description="One working conversation, strip and card stay in sync."
              items={MOCK_SINGLE}
              testId="stage-rail-page-edge-single"
            />
            <MiniDesktop
              title="Many items — scroll"
              description="Seven conversations; the expanded stack scrolls."
              items={MOCK_MANY}
              testId="stage-rail-page-edge-many"
            />
            <MiniDesktop
              title="All done"
              description="Finished work stays visible until it ages out."
              items={MOCK_ALL_DONE}
              testId="stage-rail-page-edge-all-done"
            />
          </div>
        </Section>
      </div>
    </div>
  );
}
